<template>
  <div class="stepper-wrapper">
    <div
      ref="track"
      class="stepper"
      @scroll="updateScrollShadows"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        class="stepper__node"
        :class="[`stepper__node--${item.status}`, { 'stepper__node--clickable': isClickable(item) }]"
        :data-field="dataFieldFor(index)"
        :role="isClickable(item) ? 'button' : undefined"
        :tabindex="isClickable(item) ? 0 : undefined"
        @click="handleClick(item)"
        @keydown.enter="handleClick(item)"
        @keydown.space.prevent="handleClick(item)"
      >
        <span class="stepper__line" />
        <span class="stepper__dot">
          <template v-if="item.status === 'done'">✓</template>
          <template v-else-if="item.status === 'current' || item.status === 'warning'">●</template>
          <template v-else-if="item.status === 'error'">!</template>
        </span>
        <span class="stepper__name">{{ item.name }}</span>
        <span class="stepper__caption">{{ itemCaption(item) }}</span>
      </div>
    </div>
    <div
      class="stepper-wrapper__shadow stepper-wrapper__shadow--left"
      :class="{ 'stepper-wrapper__shadow--visible': canScrollLeft }"
    />
    <div
      class="stepper-wrapper__shadow stepper-wrapper__shadow--right"
      :class="{ 'stepper-wrapper__shadow--visible': canScrollRight }"
    />
  </div>
</template>

<script>
import DataFieldByIndex from './DataFieldByIndex'

export default {
  name: 'BaseStepper',
  mixins: [DataFieldByIndex],
  props: {
    items: {
      type: Array,
      required: true,
      validator: (items) => items.every((item) => ['done', 'current', 'warning', 'future', 'error'].includes(item.status))
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  watch: {
    items() {
      this.$nextTick(this.updateScrollShadows)
    }
  },
  mounted() {
    this.updateScrollShadows()
    window.addEventListener('resize', this.updateScrollShadows)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollShadows)
  },
  methods: {
    itemCaption(item) {
      const caption = item.caption
      return caption || '—'
    },
    isClickable(item) {
      return Boolean(item.detail)
    },
    handleClick(item) {
      if (item.detail) {
        this.$emit('row-detail', { detail: item.detail })
      }
    },
    updateScrollShadows() {
      const track = this.$refs.track
      if (!track) {
        return
      }
      this.canScrollLeft = track.scrollLeft > 0
      this.canScrollRight = track.scrollLeft + track.clientWidth < track.scrollWidth - 1
    }
  }
}
</script>

<style lang="scss">
.stepper-wrapper {
  position: relative;

  &__shadow {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 3;

    &--visible {
      opacity: 1;
    }

    &--left {
      left: 0;
      background: linear-gradient(to right, rgba(0, 0, 0, 0.18), transparent);
    }

    &--right {
      right: 0;
      background: linear-gradient(to left, rgba(0, 0, 0, 0.18), transparent);
    }
  }
}

.stepper {
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  padding: 4px 2px;

  &__node {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-width: 88px;
    position: relative;

    &:first-child .stepper__line {
      display: none;
    }

    &--done .stepper__line,
    &--current .stepper__line,
    &--warning .stepper__line {
      background: var(--v-success-base);
    }

    &--done .stepper__dot {
      background: var(--v-success-base);
      border-color: var(--v-success-base);
      color: #fff;
    }

    &--current .stepper__dot {
      border-color: var(--v-primary-base);
      color: var(--v-primary-base);
      box-shadow: 0 0 0 4px rgba(64, 117, 109, 0.14);
    }

    &--warning .stepper__dot {
      border-color: var(--v-warning-base);
      color: var(--v-warning-base);
      box-shadow: 0 0 0 4px rgba(251, 140, 0, 0.14);
    }

    &--warning .stepper__name {
      color: var(--v-warning-base);
    }

    &--warning .stepper__caption {
      color: var(--v-warning-base);
      font-weight: 600;
    }

    &--future .stepper__name {
      color: var(--ds-color-black-040);
      font-weight: 500;
    }

    &--error .stepper__line {
      background: var(--v-success-base);
    }

    &--error .stepper__dot {
      background: var(--v-error-base);
      border-color: var(--v-error-base);
      color: #fff;
    }

    &--error .stepper__name,
    &--error .stepper__caption {
      color: var(--v-errorDarken-base);
    }

    &--current .stepper__name {
      color: var(--v-primary-base);
    }

    &--current .stepper__caption {
      color: var(--v-primary-base);
      font-weight: 600;
    }

    &--clickable {
      cursor: pointer;
      border-radius: 6px;

      &:hover .stepper__name {
        text-decoration: underline;
      }

      &:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 2px;
      }
    }
  }

  &__line {
    position: absolute;
    top: 11px;
    left: -50%;
    width: 100%;
    height: 2px;
    background: var(--ds-color-black-010);
    z-index: 1;
  }

  &__dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    background: var(--ds-color-white);
    border: 2px solid var(--ds-color-black-010);
    color: var(--ds-color-black-040);
    z-index: 2;
  }

  &__name {
    font-size: 0.8rem;
    font-weight: 600;
    margin-top: 8px;
    text-align: center;
    color: var(--v-textPrimary-base);
  }

  &__caption {
    font-size: 0.75rem;
    color: var(--ds-color-black-040);
    margin-top: 2px;
    text-align: center;
    overflow-wrap: anywhere;
  }
}
</style>
